import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { internalChatEndpoint } from "@/config/endpoints";
import { toast } from "sonner";
import { useChatNotesSync } from "./useLeadFormConfig";

export function useInternalChatMutations() {
  const qc = useQueryClient();
  // "Chat & notes sync": the server saves every message as a note (and edits /
  // deletes follow), so after our own write the open lead / deal drawer
  // refetches to show it in Notes. Notes have no live push of their own.
  const chatNotesSync = useChatNotesSync();
  const refreshNotes = () => {
    if (!chatNotesSync) return;
    qc.invalidateQueries({ queryKey: ["get-leads"] });
    qc.invalidateQueries({ queryKey: ["get-child"] });
  };

  /** Send a text or media message to lead or child channel. */
  const sendMessage = useMutation({
    mutationFn: async ({
      leadId,
      childId,
      body,
      type = "text",
      mediaUrl,
      mediaMime,
      mediaName,
      mediaSize,
      mentions,
      replyToId,
    }: {
      leadId: number | string;
      childId?: number | string | null;
      body?: string;
      type?: string;
      mediaUrl?: string;
      mediaMime?: string;
      mediaName?: string;
      mediaSize?: number;
      mentions?: { userId: number; name: string }[];
      replyToId?: string;
    }) => {
      const url = childId
        ? `${internalChatEndpoint}/${leadId}/child/${childId}`
        : `${internalChatEndpoint}/${leadId}`;

      return axiosInstance.post(url, {
        body,
        type,
        mediaUrl,
        mediaMime,
        mediaName,
        mediaSize,
        mentions,
        replyToId,
      });
    },
    onSuccess: () => {
      // Refetch messages so sender sees their own message immediately
      qc.invalidateQueries({ queryKey: ["internal-chat"] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
      refreshNotes();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to send message";
      toast.error(msg);
    },
  });

  /** Upload a file (document, image, audio) to S3. Returns media metadata. */
  const uploadFile = useMutation({
    mutationFn: async ({
      leadId,
      childId,
      file,
    }: {
      leadId: number | string;
      /** When set, server enforces ChildScope before S3 (child-channel uploads). */
      childId?: number | string | null;
      file: File | Blob;
    }) => {
      const formData = new FormData();
      formData.append(
        "file",
        file,
        file instanceof File ? file.name : "voice-note.webm"
      );

      const params =
        childId != null && childId !== ""
          ? `?childId=${encodeURIComponent(String(childId))}`
          : "";

      const res = await axiosInstance.post(
        `${internalChatEndpoint}/${leadId}/upload${params}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      return res.data as {
        mediaUrl: string;
        mediaMime: string;
        mediaName: string;
        mediaSize?: number;
      };
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to upload file";
      toast.error(msg);
    },
  });

  /** Delete-for-everyone (sender only) — server tombstones the message and
   *  its All-Chats mirror; open chats update live via INTERNAL_CHAT_DELETED. */
  const deleteMessage = useMutation({
    mutationFn: async ({ messageId }: { messageId: string }) => {
      return axiosInstance.delete(
        `${internalChatEndpoint}/messages/${messageId}`
      );
    },
    onSuccess: () => {
      // Socket covers open views; invalidations cover thread previews and
      // any chat opened later from cache.
      qc.invalidateQueries({ queryKey: ["internal-chat"] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
      refreshNotes();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to delete message";
      toast.error(msg);
    },
  });

  /** Edit-your-own text message (sender only). The server rewrites the
   *  child/All-Chats mirror pair together; open chats update live via
   *  INTERNAL_CHAT_EDITED, the refetch below covers cached views. */
  const editMessage = useMutation({
    mutationFn: async ({
      messageId,
      body,
      mentions,
    }: {
      messageId: string;
      body: string;
      mentions?: { userId: number; name: string }[];
    }) => {
      return axiosInstance.patch(
        `${internalChatEndpoint}/messages/${messageId}`,
        { body, mentions: mentions ?? [] }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["internal-chat"] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
      refreshNotes();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to edit message";
      toast.error(msg);
    },
  });

  /** Mark messages as read. */
  const markAsRead = useMutation({
    mutationFn: async ({
      leadId,
      childId,
      messageIds,
    }: {
      leadId: number | string;
      childId?: number | string | null;
      messageIds: string[];
    }) => {
      return axiosInstance.post(`${internalChatEndpoint}/${leadId}/read`, {
        childId: childId || null,
        messageIds,
      });
    },
    onMutate: async ({ leadId, childId, messageIds }) => {
      // Optimistic: clear the badge immediately so the user sees it drop on open.
      // We can't perfectly attribute messageIds to per-thread counts here, so we
      // zero the current thread's row and subtract its old count from the global.
      await qc.cancelQueries({ queryKey: ["chat-unread-count"] });
      await qc.cancelQueries({ queryKey: ["chat-threads"] });

      const prevCount = qc.getQueryData<number>(["chat-unread-count"]) ?? 0;
      // ["chat-threads"] is an INFINITE query (useChatThreads) — its cache is
      // `{ pages: [{ threads: [...] }], pageParams }`, NOT a flat array. The old
      // code treated it as an array and called `.find()` on the object, which
      // THREW inside onMutate and aborted the whole mutation — so the backend
      // read-POST never fired and the badge never cleared. Read the infinite
      // structure correctly and update every page.
      const prevData = qc.getQueryData<any>(["chat-threads"]);
      const prevThreads: any[] =
        prevData?.pages?.flatMap((p: any) => p.threads ?? []) ?? [];

      const matchingThread = prevThreads.find(
        (t) =>
          Number(t.leadId) === Number(leadId) &&
          (childId == null
            ? true
            : t.children?.some(
                (c: any) => Number(c.id) === Number(childId)
              ))
      );
      const decrementBy = Math.min(
        matchingThread?.unreadCount ?? messageIds.length,
        prevCount
      );

      qc.setQueryData<number>(
        ["chat-unread-count"],
        Math.max(0, prevCount - decrementBy)
      );

      if (prevData?.pages) {
        qc.setQueryData<any>(["chat-threads"], {
          ...prevData,
          pages: prevData.pages.map((page: any) => ({
            ...page,
            threads: (page.threads ?? []).map((t: any) =>
              Number(t.leadId) === Number(leadId)
                ? { ...t, unreadCount: 0 }
                : t
            ),
          })),
        });
      }

      return { prevCount, prevData };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevCount !== undefined)
        qc.setQueryData(["chat-unread-count"], ctx.prevCount);
      if (ctx?.prevData !== undefined)
        qc.setQueryData(["chat-threads"], ctx.prevData);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chat-unread-count"] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
  });

  return { sendMessage, uploadFile, markAsRead, deleteMessage, editMessage };
}
